import { PhotoSwipeLightbox } from "vendor";

const mediaGridFullscreenViewers = new WeakMap();

function getMediaGrids(root) {
  const mediaGrids = [];

  if (root instanceof Element && root.matches("media-grid")) {
    mediaGrids.push(root);
  }

  mediaGrids.push(...root.querySelectorAll("media-grid"));

  return mediaGrids;
}

function registerMediaGridPagination(lightbox) {
  lightbox.on("uiRegister", () => {
    lightbox.pswp.ui.registerElement({
      name: "bottom-bar",
      order: 5,
      appendTo: "wrapper",
      html: `
        <div class="pagination">
          <button class="pagination__item group" type="button" rel="prev">
            <span class="sr-only">${window.themeVariables.strings.accessibilityPrevious}</span>
            <span class="animated-arrow animated-arrow--reverse"></span>
          </button>

          <span class="pagination__current text-sm">
            <span class="pagination__current-page">1</span> / <span class="pagination__page-count"></span>
          </span>

          <button class="pagination__item group" type="button" rel="next">
            <span class="sr-only">${window.themeVariables.strings.accessibilityNext}</span>
            <span class="animated-arrow"></span>
          </button>
        </div>
      `,
      onInit: (element, photoswipe) => {
        const itemCount = photoswipe.getNumItems();

        element.hidden = itemCount <= 1;
        element.querySelector(".pagination__page-count").textContent = itemCount;
        element.querySelector('[rel="prev"]')?.addEventListener("click", () => photoswipe.prev());
        element.querySelector('[rel="next"]')?.addEventListener("click", () => photoswipe.next());

        photoswipe.on("change", () => {
          element.querySelector(".pagination__current-page").textContent = photoswipe.currIndex + 1;
        });
      }
    });
  });
}

function initializeMediaGridFullscreenViewers(root = document) {
  getMediaGrids(root).forEach((mediaGrid) => {
    if (mediaGridFullscreenViewers.has(mediaGrid) || !mediaGrid.querySelector("[data-media-grid-fullscreen]")) {
      return;
    }

    const lightbox = new PhotoSwipeLightbox({
      gallery: mediaGrid,
      children: "[data-media-grid-fullscreen]",
      pswpModule: () => import("photoswipe"),
      bgOpacity: 1,
      closeTitle: window.themeVariables.strings.closeGallery,
      errorMsg: window.themeVariables.strings.errorGallery,
      initialZoomLevel: "fit",
      secondaryZoomLevel: "fit",
      maxZoomLevel: "fit",
      imageClickAction: "close",
      doubleTapAction: false,
      bgClickAction: "close",
      wheelToZoom: false,
      pinchToClose: false,
      arrowPrev: false,
      arrowNext: false,
      counter: false,
      zoom: false,
      closeSVG: `<svg fill="none" xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
        <path d="M56 28C56 12.536 43.464 0 28 0S0 12.536 0 28s12.536 28 28 28 28-12.536 28-28Z" fill="#fff"/>
        <path d="M55.5 28C55.5 12.812 43.188.5 28 .5S.5 12.812.5 28 12.812 55.5 28 55.5 55.5 43.188 55.5 28Z" stroke="#252627" stroke-opacity=".12"/>
        <path d="m22.344 22.343 11.313 11.314m-11.313 0 11.313-11.313" stroke="#252627" stroke-width="2"/>
      </svg>`
    });

    lightbox.addFilter("isContentZoomable", () => false);
    registerMediaGridPagination(lightbox);
    lightbox.init();
    mediaGridFullscreenViewers.set(mediaGrid, lightbox);
  });
}

function destroyMediaGridFullscreenViewers(root) {
  getMediaGrids(root).forEach((mediaGrid) => {
    mediaGridFullscreenViewers.get(mediaGrid)?.destroy();
    mediaGridFullscreenViewers.delete(mediaGrid);
  });
}

initializeMediaGridFullscreenViewers();

document.addEventListener("shopify:section:load", (event) => {
  initializeMediaGridFullscreenViewers(event.target);
});

document.addEventListener("shopify:section:unload", (event) => {
  destroyMediaGridFullscreenViewers(event.target);
});

const linkedVariantMediaBlockCache = new Map();

class LinkedVariantMediaBlock extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this._requestIndex = 0;
    this._onVariantChangeListener = this._renderSelectedLinkedProductBlock.bind(this);

    document.addEventListener("variant:change", this._onVariantChangeListener, { signal: this._abortController.signal });
    this._renderSelectedLinkedProductBlock();
  }

  disconnectedCallback() {
    this._abortController.abort();
    this._activeFetchController?.abort();
  }

  async _renderSelectedLinkedProductBlock() {
    const linkedProductUrl = this._selectedLinkedProductUrl;

    if (!linkedProductUrl) {
      return;
    }

    const sectionId = this.getAttribute("section-id");
    const blockId = this.getAttribute("block-id");

    if (!sectionId || !blockId) {
      return;
    }

    this._activeFetchController?.abort();
    this._activeFetchController = new AbortController();

    const requestIndex = ++this._requestIndex;
    const sectionUrl = new URL(linkedProductUrl, window.location.origin);
    sectionUrl.searchParams.set("section_id", sectionId);

    const cacheKey = sectionUrl.toString();

    try {
      const cachedSectionHtml = linkedVariantMediaBlockCache.get(cacheKey);
      let sectionHtml = cachedSectionHtml;

      if (!sectionHtml) {
        const response = await fetch(sectionUrl.toString(), { signal: this._activeFetchController.signal });

        if (!response.ok) {
          throw new Error(`Unable to load linked variant media block section: ${response.status}`);
        }

        sectionHtml = await response.text();
      }

      if (!this.isConnected || requestIndex !== this._requestIndex) {
        return;
      }

      const temporaryDocument = new DOMParser().parseFromString(sectionHtml, "text/html");
      const blockDomId = `block-${sectionId}-${blockId}`;
      const matchingBlock = temporaryDocument.getElementById(blockDomId);
      const currentBlock = this.querySelector(`#${blockDomId}`);

      if (!matchingBlock || !currentBlock) {
        return;
      }

      if (!cachedSectionHtml) {
        linkedVariantMediaBlockCache.set(cacheKey, sectionHtml);
      }

      matchingBlock.querySelectorAll("noscript").forEach((noscript) => noscript.remove());
      matchingBlock.querySelectorAll("[reveal-on-scroll]").forEach((element) => element.removeAttribute("reveal-on-scroll"));
      currentBlock.innerHTML = matchingBlock.innerHTML;
    } catch (error) {
      if (error.name !== "AbortError") {
        console.warn("Unable to load linked variant media block.", error);
      }
    }
  }

  get _selectedLinkedProductUrl() {
    const variantPicker = document.querySelector(".shopify-section--main-product variant-picker[form-id]");
    const formId = variantPicker?.getAttribute("form-id");
    const form = formId ? document.getElementById(formId) : null;
    const selectedInput = Array.from(form?.elements || []).find((element) => element.matches?.("input[data-linked-product-url][data-option-position]:checked"));

    return selectedInput?.dataset.linkedProductUrl;
  }
}

if (!window.customElements.get("linked-variant-media-block")) {
  window.customElements.define("linked-variant-media-block", LinkedVariantMediaBlock);
}

class CdlPopup extends HTMLElement {
  connectedCallback() {
    const anchor = this.getAttribute("data-anchor");

    this.dialog = this.querySelector("dialog");

    if (!this.dialog || !anchor) {
      return;
    }

    this.anchor = `#${anchor}`;
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    document.addEventListener("click", this, { signal });
    this.dialog.addEventListener("click", this, { signal });

    if (window.location.hash === this.anchor) {
      this.open();
    }
  }

  disconnectedCallback() {
    this._abortController?.abort();
  }

  handleEvent(event) {
    if (event.currentTarget === this.dialog) {
      if (event.target === this.dialog || event.target.closest("[data-cdl-popup-close]")) {
        this.dialog.close();
      }

      return;
    }

    const link = event.target.closest?.("a[href]");

    if (link?.hash === this.anchor) {
      event.preventDefault();
      this.open();
    }
  }

  open() {
    if (!this.dialog.open) {
      this.dialog.showModal();
    }
  }
}

if (!window.customElements.get("cdl-popup")) {
  window.customElements.define("cdl-popup", CdlPopup);
}
